import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

// [2026-09-01] 문자발송내역 관제 화면 — 요청 → 앱처리 → 실제발송확인 3단계.
// 배경: 9/1 사고(회사폰 통신망 정지에도 DB는 전부 sent라 4일 미발견). 워치독(sms_watchdog.js)이
//   폰의 진짜 발송함과 10분마다 대사해 real_status를 채우고, 이 화면이 그 결과를 관제한다.

const Wrap = styled.div`padding: 20px; width: 100%; overflow-y: auto; background: #f7f8fa;`;
const Title = styled.h2`margin: 0 0 14px; font-size: 20px; color: #1a202c;`;
const Banner = styled.div`
  background: ${p => p.$level === 'red' ? '#fff5f5' : '#f0fff4'};
  border: 1px solid ${p => p.$level === 'red' ? '#feb2b2' : '#9ae6b4'};
  color: ${p => p.$level === 'red' ? '#c53030' : '#276749'};
  border-radius: 8px; padding: 12px 16px; margin-bottom: 14px; font-weight: 700; font-size: 14px;`;
const Cards = styled.div`display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;`;
const Card = styled.div`
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(0,0,0,.04);`;
const CardLabel = styled.div`font-size: 12px; color: #718096; margin-bottom: 6px;`;
const CardValue = styled.div`font-size: 22px; font-weight: 800; color: ${p => p.$color || '#2d3748'};`;
const CardSub = styled.div`font-size: 11px; color: #a0aec0; margin-top: 4px;`;
const FilterBar = styled.div`
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap; background: #fff;
  border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 12px; margin-bottom: 12px;`;
const Preset = styled.button`
  padding: 6px 10px; border-radius: 6px; border: 1px solid ${p => p.$on ? '#3182ce' : '#cbd5e0'};
  background: ${p => p.$on ? '#ebf8ff' : '#fff'}; color: ${p => p.$on ? '#2b6cb0' : '#4a5568'};
  font-size: 12px; cursor: pointer; font-weight: 600;`;
const Sel = styled.select`padding: 6px 8px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px;`;
const Inp = styled.input`padding: 6px 8px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px;`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e2e8f0;
  border-radius: 10px; overflow: hidden; font-size: 13px;`;
const Th = styled.th`background: #edf2f7; padding: 10px 8px; text-align: left; color: #4a5568; font-size: 12px; white-space: nowrap;`;
const Td = styled.td`padding: 9px 8px; border-top: 1px solid #edf2f7; color: #2d3748; vertical-align: middle;`;
const Tr = styled.tr`cursor: pointer; &:hover { background: #f7fafc; }`;
const Badge = styled.span`
  display: inline-block; padding: 3px 8px; border-radius: 999px; font-size: 11px; font-weight: 700;
  background: ${p => p.$bg}; color: ${p => p.$fg};`;
const Pager = styled.div`display: flex; gap: 6px; justify-content: center; margin: 14px 0;`;
const PBtn = styled.button`
  padding: 6px 11px; border-radius: 6px; border: 1px solid ${p => p.$on ? '#3182ce' : '#cbd5e0'};
  background: ${p => p.$on ? '#3182ce' : '#fff'}; color: ${p => p.$on ? '#fff' : '#4a5568'};
  font-size: 12px; cursor: pointer;`;
const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 1000;`;
const Modal = styled.div`background: #fff; border-radius: 10px; padding: 20px; width: 480px; max-width: 92%;`;

const STATUS_STYLE = {
  '발송확인':      { bg: '#c6f6d5', fg: '#22543d' },
  '앱처리(확인전)': { bg: '#e2e8f0', fg: '#4a5568' },
  '발송실패의심':   { bg: '#fed7d7', fg: '#c53030' },
  '실패':          { bg: '#fed7d7', fg: '#c53030' },
  '폰수신':        { bg: '#bee3f8', fg: '#2c5282' },
  '대기':          { bg: '#fefcbf', fg: '#975a16' },
};
const TYPE_STYLE = {
  '대행할당': { bg: '#e9d8fd', fg: '#553c9a' },
  '인증':    { bg: '#bee3f8', fg: '#2c5282' },
  '테스트':  { bg: '#e2e8f0', fg: '#4a5568' },
  '기타':    { bg: '#edf2f7', fg: '#718096' },
};

const ymd = (d) => d.toISOString().slice(0, 10);
const kstToday = () => { const n = new Date(Date.now() + 9 * 3600 * 1000); return n.toISOString().slice(0, 10); };
const daysAgo = (n) => { const d = new Date(Date.now() + 9 * 3600 * 1000); d.setUTCDate(d.getUTCDate() - n); return ymd(d); };

export default function SmsHistory() {
  const [stats, setStats] = useState(null);
  const [watchdog, setWatchdog] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [preset, setPreset] = useState('7일');
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(kstToday());
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const limit = 20;

  const applyPreset = (p) => {
    setPreset(p); setPage(1);
    if (p === '오늘') { setFrom(kstToday()); setTo(kstToday()); }
    else if (p === '7일') { setFrom(daysAgo(6)); setTo(kstToday()); }
    else if (p === '30일') { setFrom(daysAgo(29)); setTo(kstToday()); }
    else { setFrom(''); setTo(''); }
  };

  const loadStats = useCallback(() => {
    fetch('/request/sms_history_stats', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setStats(d.stats || null); setWatchdog(d.watchdog || null); })
      .catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    const ps = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (from) ps.set('from', from);
    if (to) ps.set('to', to);
    if (type) ps.set('type', type);
    if (status) ps.set('status', status);
    if (q) ps.set('q', q);
    fetch('/request/sms_history?' + ps.toString(), { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setRows(d.rows || []); setTotal(d.total || 0); })
      .catch(() => {});
  }, [page, from, to, type, status, q]);

  useEffect(() => { loadStats(); const t = setInterval(loadStats, 60000); return () => clearInterval(t); }, [loadStats]);
  useEffect(() => { loadList(); }, [loadList]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageList = [];
  for (let p = Math.max(1, page - 3); p <= Math.min(totalPages, page + 3); p++) pageList.push(p);

  const alertLevel = (watchdog && (watchdog.phone_conn !== 'ok' || watchdog.missing > 0)) ? 'red'
    : (stats && Number(stats.week_suspect) > 0) ? 'red' : 'green';
  const bannerText = !watchdog
    ? '워치독 기록이 아직 없습니다 (10분 주기 자동 대사 대기 중)'
    : watchdog.phone_conn !== 'ok'
      ? `⚠ 회사폰 USB 연결이 끊겨 실제발송 확인을 못 하고 있습니다 (마지막 확인 ${watchdog.checked_at})`
      : watchdog.missing > 0
        ? `⚠ 실제 발송이 확인되지 않은 문자 ${watchdog.missing}건 — 회사폰 통신상태를 확인하세요 (${watchdog.checked_at} 기준)`
        : `✓ 정상 — 마지막 대사 ${watchdog.checked_at}, 폰 실제 마지막 발송 ${watchdog.phone_last_real_sent || '-'}`;

  return (
    <Wrap>
      <Title>문자발송내역</Title>
      <Banner $level={alertLevel}>{bannerText}</Banner>

      <Cards>
        <Card>
          <CardLabel>오늘 발송 (확인/전체)</CardLabel>
          <CardValue>{stats ? `${stats.today_confirmed} / ${stats.today_total}` : '-'}</CardValue>
          <CardSub>실제발송 확인 건수 / 요청 건수</CardSub>
        </Card>
        <Card>
          <CardLabel>실패·의심 (7일)</CardLabel>
          <CardValue $color={stats && Number(stats.week_suspect) > 0 ? '#c53030' : '#2d3748'}>
            {stats ? stats.week_suspect : '-'}
          </CardValue>
          <CardSub>발송실패의심 + 실패</CardSub>
        </Card>
        <Card>
          <CardLabel>대기중</CardLabel>
          <CardValue>{stats ? stats.waiting : '-'}</CardValue>
          <CardSub>폰이 아직 안 가져간 문자</CardSub>
        </Card>
        <Card>
          <CardLabel>회사폰 상태</CardLabel>
          <CardValue $color={watchdog && watchdog.phone_conn === 'ok' ? '#2f855a' : '#c53030'}>
            {watchdog ? (watchdog.phone_conn === 'ok' ? '연결됨' : '끊김') : '-'}
          </CardValue>
          <CardSub>{watchdog ? `대사 ${watchdog.checked_at}` : '워치독 대기'}</CardSub>
        </Card>
      </Cards>

      <FilterBar>
        {['오늘', '7일', '30일', '전체'].map(p => (
          <Preset key={p} $on={preset === p} onClick={() => applyPreset(p)}>{p}</Preset>
        ))}
        <Inp type="date" value={from} onChange={e => { setFrom(e.target.value); setPreset(''); setPage(1); }} />
        <span>~</span>
        <Inp type="date" value={to} onChange={e => { setTo(e.target.value); setPreset(''); setPage(1); }} />
        <Sel value={type} onChange={e => { setType(e.target.value); setPage(1); }}>
          <option value="">유형 전체</option>
          <option>대행할당</option><option>인증</option><option>테스트</option><option>기타</option>
        </Sel>
        <Sel value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">상태 전체</option>
          <option>발송확인</option><option>앱처리(확인전)</option><option>발송실패의심</option>
          <option>실패</option><option>폰수신</option><option>대기</option>
        </Sel>
        <Inp placeholder="수신자명/번호/내용 검색" value={q}
             onChange={e => { setQ(e.target.value); setPage(1); }} style={{ minWidth: 200 }} />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#718096' }}>{total.toLocaleString()}건</span>
      </FilterBar>

      <Table>
        <thead>
          <tr>
            <Th style={{ width: 140 }}>요청시각</Th>
            <Th style={{ width: 150 }}>수신자</Th>
            <Th style={{ width: 80 }}>유형</Th>
            <Th>내용</Th>
            <Th style={{ width: 140 }}>앱처리</Th>
            <Th style={{ width: 150 }}>실제발송</Th>
            <Th style={{ width: 110 }}>상태</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><Td colSpan={7} style={{ textAlign: 'center', color: '#a0aec0', padding: 30 }}>조회 결과가 없습니다</Td></tr>
          )}
          {rows.map(r => {
            const ss = STATUS_STYLE[r.disp_status] || { bg: '#edf2f7', fg: '#4a5568' };
            const ts = TYPE_STYLE[r.sms_type] || TYPE_STYLE['기타'];
            return (
              <Tr key={r.id} onClick={() => setDetail(r)}>
                <Td>{r.req_time_kst}</Td>
                <Td>
                  <b>{r.recipient_name || '미등록'}</b>
                  <div style={{ fontSize: 11, color: '#a0aec0' }}>{r.sender_num}</div>
                </Td>
                <Td><Badge $bg={ts.bg} $fg={ts.fg}>{r.sms_type}</Badge></Td>
                <Td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {String(r.send_text || '').replace(/\n/g, ' ')}
                </Td>
                <Td style={{ fontSize: 12, color: '#718096' }}>{r.app_time_kst || '-'}</Td>
                <Td>
                  {r.real_status === 'confirmed'
                    ? <span style={{ color: '#2f855a', fontWeight: 700 }}>✓ {r.real_time_kst}</span>
                    : r.real_status === 'missing'
                      ? <span style={{ color: '#c53030', fontWeight: 700 }}>⚠ 미확인</span>
                      : <span style={{ color: '#a0aec0' }}>–</span>}
                </Td>
                <Td><Badge $bg={ss.bg} $fg={ss.fg}>{r.disp_status}</Badge></Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>

      <Pager>
        <PBtn onClick={() => setPage(1)} disabled={page === 1}>«</PBtn>
        {pageList.map(p => <PBtn key={p} $on={p === page} onClick={() => setPage(p)}>{p}</PBtn>)}
        <PBtn onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</PBtn>
      </Pager>

      {detail && (
        <Overlay onClick={() => setDetail(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 10px' }}>문자 상세 #{detail.id}</h3>
            <div style={{ fontSize: 13, color: '#4a5568', lineHeight: 1.9 }}>
              <div><b>수신자:</b> {detail.recipient_name || '미등록'} ({detail.sender_num})</div>
              <div><b>요청시각:</b> {detail.req_time_kst}</div>
              <div><b>앱처리:</b> {detail.app_time_kst || '-'}</div>
              <div><b>실제발송:</b> {detail.real_status === 'confirmed' ? '✓ ' + detail.real_time_kst : detail.real_status === 'missing' ? '⚠ 미확인' : '– (대사 전)'}</div>
              <div><b>상태:</b> {detail.disp_status}</div>
            </div>
            <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginTop: 10, whiteSpace: 'pre-wrap', fontSize: 13 }}>
              {detail.send_text}
            </div>
            <div style={{ textAlign: 'right', marginTop: 14 }}>
              <PBtn onClick={() => setDetail(null)}>닫기</PBtn>
            </div>
          </Modal>
        </Overlay>
      )}
    </Wrap>
  );
}
